import type { SessionKanjiResult } from '@kanjido/database'

// Surface C row 3 — Per-kanji result tiles. Marginalia register, three ink-tones,
// no chromatic signal (§12.2 ban: NO green/red on the result grid).
//
// The mapping:
//   first-try   → text-ink     (full ink — the kanji landed clean)
//   corrected   → text-body-fg (mid ink — landed eventually)
//   missed      → text-muted-fg (low ink — flagged for re-encoding)
//
// All three tones share the same tile size, the same mincho weight, the same
// hairline border. The state lives in the ink alone — no badge, no glow, no
// shake, no icon. Factual reflection. The kanji's own typographic weight is the
// reward for first-try; the muted-fg is the cue to look again.
//
// Frame number sits below in tabular `text-meta` — same micro-marginalia register
// the corpus uses everywhere. No meaning gloss inline (the user just spent ten
// minutes on these kanji; spelling them out under each tile would patronize).
//
// Used by Learn-C and Review-B (which inherits Learn-C's classifier). Quiz-D
// and Challenge-C/D have their own bucket logic and keep path-specific grids.

type Tier = 'first-try' | 'corrected' | 'missed'

// Straight lookup (spec §2): the tone IS the field. `overridden` renders in
// the corrected tone — the completed pages' override sub-line (§8.5) is the
// self-grade marker; the grid stays three ink-tones.
function classify(r: SessionKanjiResult): Tier {
    switch (r.outcome) {
        case 'first_try':
            return 'first-try'
        case 'corrected':
        case 'overridden':
            return 'corrected'
        default:
            return 'missed'
    }
}

const TIER_INK: Record<Tier, string> = {
    'first-try': 'text-ink',
    corrected: 'text-body-fg',
    missed: 'text-muted-fg',
}

const TIER_LABEL: Record<Tier, string> = {
    'first-try': 'first try',
    corrected: 'corrected',
    missed: 'missed',
}

interface Props {
    results: SessionKanjiResult[]
    ariaLabel?: string
    // Landing beat 05 fills the grid as the visitor scrolls: cells at
    // index >= revealed are laid out but invisible. Default: all shown.
    revealed?: number
}

export function KanjiResultGrid({
    results,
    ariaLabel = 'Kanji studied this batch',
    revealed = results.length,
}: Props) {
    if (results.length === 0) return null
    return (
        <ul
            className="mt-8 grid grid-cols-4 gap-px overflow-hidden border border-rule-soft sm:grid-cols-5 md:mt-10 md:grid-cols-6"
            aria-label={ariaLabel}
        >
            {results.map((r, i) => {
                const tier = classify(r)
                const ink = TIER_INK[tier]
                const hidden = i >= revealed ? ' opacity-0' : ''
                return (
                    <li
                        key={r.frameNumber}
                        className={`flex flex-col items-center gap-1 bg-paper px-2 py-3 outline outline-offset-0 outline-rule-soft transition-opacity duration-200 sm:py-4${hidden}`}
                        title={`${r.character} — ${r.meaning} — ${TIER_LABEL[tier]}`}
                    >
                        <span
                            className={`font-mincho text-2xl leading-none select-none sm:text-3xl ${ink}`}
                        >
                            {r.character}
                        </span>
                        <span className="text-meta tracking-[0.12em] text-muted-fg uppercase tabular-nums">
                            {Number.isInteger(r.frameNumber)
                                ? r.frameNumber
                                : r.frameNumber.toFixed(2)}
                        </span>
                        <span className="sr-only">{TIER_LABEL[tier]}</span>
                    </li>
                )
            })}
        </ul>
    )
}
